import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Lightbulb,
  ArrowFatUp,
  CheckCircle,
  Clock,
  XCircle,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Ideas() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "feature",
  });

  useEffect(() => {
    fetchIdeas();
  }, []);

  const fetchIdeas = async () => {
    try {
      const response = await fetch(`${API}/support/ideas`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        // Filter out declined ideas - contractors should not see declined suggestions
        const visibleIdeas = data.ideas.filter(idea => idea.status !== "declined");
        setIdeas(visibleIdeas);
      }
    } catch (error) {
      console.error("Error fetching ideas:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/support/ideas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Enhancement idea submitted");
        setIsDialogOpen(false);
        setFormData({
          title: "",
          description: "",
          category: "feature",
        });
        fetchIdeas();
      } else {
        toast.error("Failed to submit idea");
      }
    } catch (error) {
      toast.error("Failed to submit idea");
    }
  };

  const upvoteIdea = async (ideaId) => {
    try {
      const response = await fetch(`${API}/support/ideas/${ideaId}/upvote`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        fetchIdeas();
      }
    } catch (error) {
      toast.error("Failed to upvote");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "planned":
        return <Clock size={14} className="text-blue-500" />;
      case "in_progress":
        return <Clock size={14} className="text-amber-500" />;
      case "released":
        return <CheckCircle size={14} className="text-emerald-500" />;
      case "declined":
        return <XCircle size={14} className="text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: "bg-zinc-100 text-zinc-700 border-zinc-300",
      planned: "bg-blue-50 text-blue-700 border-blue-300",
      in_progress: "bg-amber-50 text-amber-700 border-amber-300",
      released: "bg-emerald-50 text-emerald-700 border-emerald-300",
      declined: "bg-red-50 text-red-700 border-red-300",
    };
    return colors[status] || colors.submitted;
  };

  return (
    <div className="space-y-6" data-testid="ideas-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold uppercase">
            Enhancement Ideas
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Vote on features you want to see or submit your own ideas
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
              data-testid="submit-idea-btn"
            >
              <Plus size={18} className="mr-2" />
              Submit Idea
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase">
                Submit Enhancement Idea
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Brief title for your idea"
                  className="mt-1 h-12 border-2"
                  required
                  data-testid="idea-title-input"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger className="mt-1 h-12 border-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feature">New Feature</SelectItem>
                    <SelectItem value="improvement">Improvement</SelectItem>
                    <SelectItem value="integration">Integration</SelectItem>
                    <SelectItem value="mobile">Mobile App</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your idea in detail. What problem does it solve?"
                  className="mt-1 border-2"
                  rows={5}
                  required
                  data-testid="idea-description-input"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
                  data-testid="submit-idea-form-btn"
                >
                  Submit Idea
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Ideas List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-zinc-200 rounded-sm animate-pulse" />
          ))}
        </div>
      ) : ideas.length > 0 ? (
        <div className="space-y-4">
          {ideas.map((idea) => (
            <Card key={idea.idea_id} className="border-2 border-zinc-200">
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {/* Upvote Button */}
                  <button
                    onClick={() => upvoteIdea(idea.idea_id)}
                    className={`flex flex-col items-center justify-center w-16 h-16 border-2 rounded-sm transition-colors ${
                      idea.upvoted_by?.includes("current_user")
                        ? "border-[#FF6600] bg-[#FF6600]/10 text-[#FF6600]"
                        : "border-zinc-200 hover:border-[#FF6600] hover:bg-[#FF6600]/5"
                    }`}
                    data-testid={`upvote-${idea.idea_id}`}
                  >
                    <ArrowFatUp size={24} weight="bold" />
                    <span className="font-heading font-bold">{idea.upvotes}</span>
                  </button>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-medium">{idea.title}</h3>
                        <p className="text-sm text-zinc-500 mt-1 line-clamp-2">
                          {idea.description}
                        </p>
                      </div>
                      <Badge variant="outline" className={getStatusColor(idea.status)}>
                        {getStatusIcon(idea.status)}
                        <span className="ml-1">{idea.status}</span>
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-zinc-400">
                      <span>{idea.user_name}</span>
                      <span>•</span>
                      <span>{new Date(idea.created_at).toLocaleDateString()}</span>
                      <Badge variant="secondary" className="text-xs">
                        {idea.category}
                      </Badge>
                    </div>
                    {idea.admin_response && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                        <p className="text-xs text-blue-600 font-medium mb-1">
                          Team Response:
                        </p>
                        <p className="text-sm text-blue-800">{idea.admin_response}</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="p-12 text-center">
            <Lightbulb size={48} className="mx-auto mb-4 text-zinc-300" />
            <h3 className="font-heading text-lg font-semibold uppercase mb-2">
              No Ideas Yet
            </h3>
            <p className="text-zinc-500 mb-6">
              Be the first to submit an enhancement idea!
            </p>
            <Button
              onClick={() => setIsDialogOpen(true)}
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
            >
              <Plus size={18} className="mr-2" />
              Submit Idea
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
